import React, { useCallback, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { faRotateRight } from "@fortawesome/pro-regular-svg-icons/faRotateRight";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons/faArrowDownToLine";

import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";
import {
  useElementValue,
  useIsReadOnly,
} from "../DocumentView/DocumentContext";
import HiddenElementBanner from "./HiddenElementBanner";
import ArchivedElementBanner from "./ArchivedElementBanner";
import { cn } from "../lib/utils";
import {
  type FileRef,
  fileMatchesAccept,
  useFormResultUpload,
  useResultFile,
} from "./useFormResultUpload";
import { FieldHeader, FieldHelpText } from "./FieldParts";
import { useImageLightbox } from "./ImageLightbox";

interface Props {
  element: DocumentElementType;
}

function valueToList(raw: unknown): FileRef[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(isFileRef);
  if (isFileRef(raw)) return [raw];
  return [];
}

/**
 * True for a file drag over the field itself. The image preview is portalled
 * out of the field but still bubbles to it through React, so a drag over the
 * preview (its target sits outside the field's DOM) is not the field's.
 */
function isFileDragOnField(ev: React.DragEvent<HTMLElement>): boolean {
  return (
    ev.currentTarget.contains(ev.target as Node | null) &&
    Array.from(ev.dataTransfer?.types ?? []).includes("Files")
  );
}

function isFileRef(v: unknown): v is FileRef {
  return (
    !!v &&
    typeof v === "object" &&
    typeof (v as { fileId?: unknown }).fileId === "number" &&
    typeof (v as { name?: unknown }).name === "string"
  );
}

const ImageUpload: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    multiple?: boolean;
  };
  const label = data?.label || "Upload Image";
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = !!data?.required;
  const multiple = !!data?.multiple;

  const [rawValue, setValue] = useElementValue<FileRef | FileRef[] | null>(
    element.id,
  );
  const files = valueToList(rawValue);
  const { pending, pickFiles, clearPending } = useFormResultUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const isReadOnly = useIsReadOnly();
  const canAdd = !isReadOnly && (multiple || files.length === 0);
  const addLabel =
    files.length > 0 ? "Add more" : multiple ? "Add images" : "Add an image";

  const onPick = useCallback(
    async (picked: File[]) => {
      if (picked.length === 0) return;
      const refs = await pickFiles(multiple ? picked : picked.slice(0, 1));
      if (refs.length === 0) return;
      if (multiple) {
        setValue([...files, ...refs]);
      } else {
        setValue(refs[0]);
      }
    },
    [pickFiles, multiple, setValue, files],
  );

  const onDrop = useCallback(
    (ev: React.DragEvent<HTMLElement>) => {
      if (!canAdd || !isFileDragOnField(ev)) return;
      ev.preventDefault();
      setDragActive(false);
      const dropped = Array.from(ev.dataTransfer?.files ?? []).filter((f) =>
        fileMatchesAccept(f, "image/*"),
      );
      if (dropped.length > 0) void onPick(dropped);
    },
    [onPick, canAdd],
  );

  const handleRemove = useCallback(
    (fileId: number) => {
      if (multiple) {
        setValue(files.filter((f) => f.fileId !== fileId));
      } else {
        setValue(null);
      }
    },
    [files, multiple, setValue],
  );

  return (
    // The whole field takes a drop, so the add row can stay slim.
    <div
      className="space-y-2"
      onDragOver={(ev) => {
        if (!canAdd || !isFileDragOnField(ev)) return;
        ev.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(ev) => {
        if (ev.currentTarget.contains(ev.relatedTarget as Node | null)) return;
        setDragActive(false);
      }}
      onDrop={onDrop}
    >
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <input
        ref={inputRef}
        id={element.id}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(ev) => {
          const list = ev.target.files;
          if (!list) return;
          const arr = Array.from(list);
          ev.target.value = "";
          void onPick(arr);
        }}
      />
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-[9px]">
          {files.map((f) => (
            <ImageTile
              key={f.fileId}
              fileRef={f}
              onRemove={isReadOnly ? undefined : () => handleRemove(f.fileId)}
            />
          ))}
        </div>
      )}
      {/* Add row: one line high, laid out like the choice rows it sits under. */}
      {canAdd && (
        <button
          type="button"
          // Every image field has the same visible text: name the field too.
          aria-label={`${addLabel}, ${label}`}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex min-h-[52px] w-full cursor-pointer items-center gap-[11px] rounded-[10px] border-[1.6px] border-dashed px-[13px] text-left text-[13.5px] leading-[1.2] transition-colors focus-visible:border-[var(--forms-accent,var(--forms-accent-default))] focus-visible:shadow-[0_0_0_3px_var(--forms-accent-ring,var(--forms-accent-ring-default))] focus-visible:outline-none",
            dragActive
              ? "border-[var(--forms-accent,var(--forms-accent-default))] bg-[var(--forms-accent-soft,var(--forms-accent-soft-default))]"
              : "border-[#c6cfdb] bg-[#fafbfc] hover:border-[var(--forms-accent-50,var(--forms-accent-50-default))] hover:bg-white",
          )}
        >
          <span className="flex h-[19px] w-[19px] flex-none items-center justify-center text-[var(--forms-accent,var(--forms-accent-default))]">
            <FontAwesomeIcon
              icon={dragActive ? faArrowDownToLine : faPlus}
              style={{ fontSize: 15 }}
            />
          </span>
          <span className="min-w-0">
            <span className="font-bold text-[var(--forms-accent,var(--forms-accent-default))]">
              {dragActive ? "Drop to add" : addLabel}
            </span>
            {!dragActive && (
              <span className="ml-[3px] text-[var(--forms-muted)] [@media(pointer:coarse)]:hidden">
                {multiple ? " or drop them here" : " or drop it here"}
              </span>
            )}
          </span>
        </button>
      )}
      {pending.length > 0 && (
        <div className="space-y-1">
          {pending.map((p) => (
            <div
              key={p.uploadId}
              className={`rounded-md border px-3 py-2 text-sm ${
                p.state === "error"
                  ? "border-destructive/30 bg-destructive/5"
                  : "border-border bg-muted/20"
              }`}
            >
              <div className="flex items-center gap-2">
                {p.state === "error" ? null : (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-3 w-3 animate-spin text-muted-foreground"
                  />
                )}
                <span className="flex-1 truncate">{p.file.name}</span>
                {p.state === "error" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        clearPending(p.uploadId);
                        void onPick([p.file]);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Retry upload"
                    >
                      <FontAwesomeIcon
                        icon={faRotateRight}
                        className="h-3 w-3"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => clearPending(p.uploadId)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label="Dismiss"
                    >
                      <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {p.progress}%
                  </span>
                )}
              </div>
              {p.state === "error" && p.errorMessage && (
                <p className="mt-1 text-xs text-destructive">
                  {p.errorMessage}
                </p>
              )}
              {p.state !== "error" && (
                <div className="mt-1 h-1 overflow-hidden rounded bg-muted">
                  <div
                    className="h-full bg-[var(--forms-accent,var(--forms-accent-default))] transition-all"
                    style={{ width: `${p.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

interface ImageTileProps {
  fileRef: FileRef;
  onRemove?: () => void;
}

const ImageTile: React.FC<ImageTileProps> = ({ fileRef, onRemove }) => {
  const { file, loading } = useResultFile(fileRef.fileId);
  const { openImage, lightbox } = useImageLightbox();
  return (
    <div className="group relative aspect-square overflow-hidden rounded-[11px] bg-[var(--forms-page)]">
      {file?.url ? (
        <button
          type="button"
          onClick={() =>
            openImage({
              fileId: fileRef.fileId,
              name: fileRef.name,
              previewUrl: file.thumbnailUrl ?? file.url,
              fullUrl: file.url,
            })
          }
          className="block h-full w-full cursor-zoom-in"
          aria-label={`View ${fileRef.name}`}
        >
          <img
            src={file.thumbnailUrl ?? file.url}
            alt={fileRef.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        </button>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          {loading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              className="h-4 w-4 animate-spin text-[var(--forms-optional)]"
            />
          ) : (
            <FontAwesomeIcon
              icon={faImage}
              className="h-6 w-6 text-[var(--forms-optional)]"
            />
          )}
        </div>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className="absolute right-[5px] top-[5px] flex h-[20px] w-[20px] items-center justify-center rounded-full text-white transition-transform hover:scale-110"
          style={{ backgroundColor: "rgba(16,24,40,.5)" }}
          aria-label={`Remove ${fileRef.name}`}
        >
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 11 }} />
        </button>
      )}
      {lightbox}
    </div>
  );
};

export default ImageUpload;
